import type {
  Color,
  DesignResponse,
  DesignViewResponse,
  Product,
  ProjectItem,
  SizeQuantity,
} from '@customink/project-service-react-components';
import { useForm, useWatch } from 'react-hook-form';

import {
  HIGH_COLOR_DESIGN_COLOR_COUNT,
  HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY,
  SLEEVE_VIEW_NAMES,
  SLEEVE_VIEW_PRESENT_MIN_QUANTITY,
} from '@/constants';

export type ProjectItemProductData = {
  productId: number;
  sizes: string[];
  color: Color;
  name: string;
  hasLineup: boolean;
  fitted: boolean;
  minQuantity: number;
  dtgEnabled: boolean;
};

type ProductColorDataMap = Record<string, ProjectItemProductData>;

type SizeQuantityMap = {
  [size: string]: number;
};

export type FormValues = {
  items: {
    id: string;
    productColorId: number;
    sizes: SizeQuantityMap;
  }[];
};

const isSleeveViewPresent = (views: DesignViewResponse[]) => {
  return SLEEVE_VIEW_NAMES.some((view) =>
    views.some((v) => v.name === view && v.icons.length > 0),
  );
};

const getMinQuantity = (
  product: Product,
  color: Color,
  designColorCount: number,
  hasSleeveView: boolean,
) => {
  const hasSinglesSelected = color?.singles_enabled;
  if (
    !color?.dtg_enabled &&
    designColorCount >= HIGH_COLOR_DESIGN_COLOR_COUNT
  ) {
    return HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY;
  }

  const productMinQuantity = hasSinglesSelected
    ? product?.minimum_quantity
    : product?.minimum_bulk_quantity;

  if (hasSleeveView) {
    return Math.max(productMinQuantity ?? 0, SLEEVE_VIEW_PRESENT_MIN_QUANTITY);
  }

  return productMinQuantity;
};

const createProductSizesMap = (
  products: Product[],
  designColorCount: number,
  hasSleeveView: boolean,
): ProductColorDataMap =>
  products.reduce((acc, product) => {
    const productEntries = product.colors.map((color) => [
      color.id,
      {
        productId: product.id,
        sizes: product.sizes,
        color,
        hasLineup: product.has_lineup,
        name: product.name,
        fitted: product.fitted,
        minQuantity:
          getMinQuantity(product, color, designColorCount, hasSleeveView) || 0,
      },
    ]);
    return Object.fromEntries([...Object.entries(acc), ...productEntries]);
  }, {} as ProductColorDataMap);

const getSizeQuantity = (
  sizesQuantities: SizeQuantity[] | undefined,
  size: string,
) => sizesQuantities?.find((sq) => sq.size === size)?.quantity ?? 0;

const createItemGroupData = (
  projectItems: ProjectItem[],
  productSizes: Record<string, ProjectItemProductData>,
) =>
  projectItems.map((item) => ({
    id: item.id,
    productColorId: item.product?.colorId,
    sizes: item.product?.colorId
      ? productSizes[item.product.colorId]?.sizes.reduce((acc, size) => {
          const quantity = getSizeQuantity(item.sizesQuantities, size);
          return quantity ? { ...acc, [size]: quantity } : acc;
        }, {} as SizeQuantityMap) ?? {}
      : {},
  }));

export function useGroupSizesForm(
  products: Product[],
  projectItems: ProjectItem[],
  designColorCount: number,
  views: DesignResponse['views'],
) {
  const hasSleeveView = isSleeveViewPresent(views);
  const productSizesMap = createProductSizesMap(
    products,
    designColorCount,
    hasSleeveView,
  );
  const itemGroupData = createItemGroupData(projectItems, productSizesMap);
  const groupMinQuantity = projectItems.reduce(
    (acc: number, item: ProjectItem) => {
      if (!item.product?.colorId) {
        return acc;
      }

      const productData = productSizesMap[item.product.colorId];
      if (productData?.minQuantity > acc) {
        acc = productData.minQuantity;
      }
      return acc;
    },
    0,
  );
  const methods = useForm<FormValues>({
    defaultValues: {
      items: itemGroupData,
    },
  });

  const currentItems = useWatch<FormValues, 'items'>({
    control: methods.control,
    name: 'items',
  });

  const totalQuantity = currentItems.reduce((acc: number, item) => {
    return (
      acc +
      Object.values(item.sizes).reduce(
        (sum: number, quantity: number) => sum + (Number(quantity) || 0),
        0,
      )
    );
  }, 0);

  return {
    methods,
    currentItems,
    totalQuantity,
    itemGroupData,
    productSizesMap,
    groupMinQuantity,
  };
}
